import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { SectionTitle, fmtInstant } from "./PilotBits";
import { MemberAvatar } from "./PilotTeam";
import { MAX_REPORT_CONTENT, MAX_REPORT_TITLE, REPORT_KINDS, REPORT_KIND_LABELS, toLocalInput, validateReport } from "../lib/pilots";
import { AlertTriangle, Check, History, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { toast } from "sonner";
import DeleteWithReasonDialog from "./DeleteWithReasonDialog";


// « Réalisé par » (facultatif) : who actually did the work, among the pilot's current team. Someone credited earlier who has since left the
// team stays listed (crossed out) so the edit can drop them, but cannot be picked again: the server would refuse it.
function PerformersField({ performers, value, onChange }) {
  const selected = new Set(value.map((p) => `${p.kind}:${p.id}`));
  const departed = value.filter((p) => !performers.some((o) => o.key === `${p.kind}:${p.id}`));
  const toggle = (option) => onChange(selected.has(option.key)
    ? value.filter((p) => `${p.kind}:${p.id}` !== option.key)
    : [...value, { kind: option.kind, id: option.id, name: option.name }]);
  if (!performers.length && !departed.length) return null;
  return (
    <div className="space-y-1.5" data-testid="report-performers">
      <Label>Réalisé par (facultatif)</Label>
      <p className="text-xs text-slate-500">Si quelqu'un d'autre que vous a fait le travail. Vous restez indiqué comme auteur de la saisie.</p>
      <div className="flex flex-wrap gap-1.5">
        {performers.map((o) => {
          const on = selected.has(o.key);
          return (
            <button key={o.key} type="button" aria-pressed={on} onClick={() => toggle(o)} data-testid={`performer-${o.key}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jamora/40 ${on ? "border-jamora bg-jamora/10 text-jamora font-medium" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {on && <Check size={12} />}{o.name}{o.detail && <span className="text-slate-400 font-normal">· {o.detail}</span>}
            </button>
          );
        })}
        {departed.map((p) => (
          <button key={`${p.kind}:${p.id}`} type="button" aria-label={`Retirer ${p.name || "cette personne"}`} onClick={() => onChange(value.filter((v) => v !== p))}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-400 line-through hover:bg-slate-100">
            {p.name || "Ancien membre"}<X size={12} />
          </button>
        ))}
      </div>
    </div>
  );
}

export function ReportDialog({ open, report, busy, onClose, onSubmit, performers = [] }) {
  const blank = { kind: "visit", title: "", content: "", occurred_at: "", performed_by: [] };
  const [form, setForm] = useState(blank);
  useEffect(() => {
    if (open) {
      setForm(report
        ? { kind: report.kind, title: report.title || "", content: report.content || "", occurred_at: toLocalInput(report.occurred_at), performed_by: report.performed_by || [] }
        : blank);
    }
  }, [open, report]); // eslint-disable-line react-hooks/exhaustive-deps
  const error = validateReport(form);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="report-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{report ? "Modifier le compte rendu" : "Nouveau compte rendu"}</DialogTitle>
          <DialogDescription>{report ? "L'ancienne version reste consultable dans l'historique." : "Visible par toute l'équipe du pilote, pas par le reste du CRM."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="report-kind">Type</Label>
            <Select value={form.kind} onValueChange={set("kind")}>
              <SelectTrigger id="report-kind" data-testid="report-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{REPORT_KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="report-date">Date et heure</Label>
            <Input id="report-date" type="datetime-local" value={form.occurred_at} onChange={(e) => set("occurred_at")(e.target.value)} data-testid="report-date" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="report-title">Titre (facultatif)</Label>
          <Input id="report-title" maxLength={MAX_REPORT_TITLE} value={form.title} onChange={(e) => set("title")(e.target.value)} data-testid="report-title" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="report-content">Compte rendu</Label>
          <Textarea id="report-content" rows={7} maxLength={MAX_REPORT_CONTENT} value={form.content} onChange={(e) => set("content")(e.target.value)} data-testid="report-content" />
          <div className="text-right text-2xs text-slate-400">{form.content.length} / {MAX_REPORT_CONTENT}</div>
        </div>
        <PerformersField performers={performers} value={form.performed_by} onChange={set("performed_by")} />
        {error && form.content.length > 0 && <p role="alert" className="text-xs text-red-600" data-testid="report-error">{error}</p>}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button  disabled={busy || !!error} onClick={() => onSubmit(form)} data-testid="report-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{report ? "Enregistrer" : "Publier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportMeta({ report }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
        <span className="font-medium text-slate-800">{report.author_name || "Compte supprimé"}</span>
        <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 font-semibold" data-testid="report-kind-badge">{REPORT_KIND_LABELS[report.kind] || report.kind}</span>
        <span>{fmtInstant(report.occurred_at)}</span>
        {report.edited && <span className="text-slate-400" data-testid="report-edited">· modifié</span>}
      </div>
      {report.performed_by?.length > 0 && (
        <div className="text-xs text-slate-600 mt-0.5" data-testid="report-performed-by">
          Réalisé par <span className="font-medium text-slate-800">{report.performed_by.map((p) => p.name || "Ancien membre").join(", ")}</span>
          <span className="text-slate-400"> · saisi par {report.author_name || "un compte supprimé"}</span>
        </div>
      )}
    </>
  );
}

// A long report is read in a side panel (fixed heading, scrolling text, « Modifier » always in view) rather than unfolded in the feed,
// where it pushed the rest of the pilot page far down and its « Réduire » out of sight.
function ReportReader({ report, open, onClose, onEdit }) {
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="report-reader">
        <SheetHeader className="border-b border-slate-100 p-5 pr-12 text-left space-y-1">
          <SheetTitle className="text-base">{report.title || REPORT_KIND_LABELS[report.kind] || "Compte rendu"}</SheetTitle>
          <SheetDescription asChild><div><ReportMeta report={report} /></div></SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap break-words" data-testid="report-reader-text">{report.content}</p>
        </div>
        {report.can_edit && (
          <div className="border-t border-slate-100 p-4 flex justify-end">
            <Button variant="outline" onClick={() => { onClose(); onEdit(report); }} data-testid="report-reader-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ReportItem({ report, onEdit, onDelete }) {
  const [reading, setReading] = useState(false);
  const [history, setHistory] = useState(false);
  const long = (report.content || "").length > 280 || (report.content || "").split("\n").length > 4;
  return (
    <li className="py-3" data-testid={`report-${report.id}`} data-mine={report.is_mine}>
      <div className="flex items-start gap-2">
        <MemberAvatar member={{ name: report.author_name, avatar_url: report.author_avatar_url }} size={24} />
        <div className="min-w-0 flex-1">
          <ReportMeta report={report} />
          {report.title && <div className="text-sm font-medium text-slate-900 mt-0.5">{report.title}</div>}
          <p className={`text-sm text-slate-700 whitespace-pre-wrap break-words mt-0.5 ${long ? "line-clamp-4" : ""}`} data-testid="report-content-text">{report.content}</p>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
            {long && <button type="button" className="text-jamora hover:underline" onClick={() => setReading(true)} data-testid={`report-read-${report.id}`}>Voir tout</button>}
            {report.revisions_count > 0 && <button type="button" className="inline-flex items-center gap-1 text-slate-500 hover:underline" onClick={() => setHistory(!history)} data-testid={`report-history-${report.id}`}><History size={12} />Historique ({report.revisions_count})</button>}
            {report.can_edit && <button type="button" className="inline-flex items-center gap-1 text-jamora hover:underline" onClick={() => onEdit(report)} data-testid={`report-edit-${report.id}`}><Pencil size={12} />Modifier</button>}
            {report.can_delete && <button type="button" className="inline-flex items-center gap-1 text-red-600 hover:underline" onClick={() => onDelete(report)} data-testid={`report-delete-${report.id}`}><Trash2 size={12} />Supprimer</button>}
          </div>
          {history && (
            <ul className="mt-2 space-y-2 border-l-2 border-slate-200 pl-3" data-testid="report-revisions">
              {[...report.revisions].reverse().map((r, i) => (
                <li key={i} className="text-xs text-slate-500">
                  <div>Version remplacée le {fmtInstant(r.superseded_at)}{r.superseded_by_name ? ` par ${r.superseded_by_name}` : ""} · {REPORT_KIND_LABELS[r.kind] || r.kind}{r.title ? ` · ${r.title}` : ""}</div>
                  <div className="whitespace-pre-wrap break-words text-slate-600">{r.content}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {long && <ReportReader report={report} open={reading} onClose={() => setReading(false)} onEdit={onEdit} />}
    </li>
  );
}

// The team's operational reports: a feed, newest first. Reading needs only membership; writing needs the pilot to be running and `manage_pilot`.
export default function PilotReportsCard({ reports, canWrite, canDelete = false, prospectId, onCreate, onEdit, onMore, onRetry }) {
  const [deleting, setDeleting] = useState(null);
  const confirmDelete = async (reason) => {
    const target = deleting;
    try {
      await api.delete(`/prospects/${prospectId}/pilot/reports/${target.id}`, { data: { reason } });
      toast.success("Compte rendu mis à la corbeille");
      onRetry();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer ce compte rendu."));
    } finally {
      setDeleting(null);
    }
  };
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-reports-card">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle hint="Visibles par l'équipe du pilote">Comptes rendus</SectionTitle>
        {canWrite && <Button variant="outline" size="sm" className="shrink-0 -mt-1" onClick={onCreate} data-testid="report-create"><Plus size={14} className="mr-1" />Nouveau</Button>}
      </div>
      {reports.status === "loading" && <div className="space-y-2" data-testid="reports-loading"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {reports.status === "error" && (
        <div className="text-center py-4 space-y-2" data-testid="reports-error">
          <AlertTriangle className="mx-auto text-amber-500" size={18} />
          <p className="text-sm text-slate-600">{reports.error}</p>
          <Button variant="outline" size="sm" onClick={onRetry}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      )}
      {reports.status === "ready" && reports.items.length === 0 && (
        <p className="text-sm text-slate-500 py-2" data-testid="reports-empty">Aucun compte rendu pour le moment.{canWrite && " Notez ici ce qui s'est passé sur le terrain : visite, appel, formation…"}</p>
      )}
      {reports.status === "ready" && reports.items.length > 0 && (
        <>
          <ul className="divide-y divide-slate-100" data-testid="reports-list">{reports.items.map((r) => <ReportItem key={r.id} report={r} onEdit={onEdit} onDelete={setDeleting} />)}</ul>
          {reports.hasMore && (
            <div className="pt-2 text-center">
              <Button variant="outline" size="sm" disabled={reports.loadingMore} onClick={onMore} data-testid="reports-more">
                {reports.loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Voir les plus anciens
              </Button>
            </div>
          )}
        </>
      )}
      <DeleteWithReasonDialog
        target={deleting} testId="delete-report" onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title="Supprimer ce compte rendu ?"
        description="Il va dans la corbeille : vous pourrez le restaurer pendant 30 jours."
      />
    </Card>
  );
}
